import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Grid,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AttachMoney from '@mui/icons-material/AttachMoney';
import AccessTime from '@mui/icons-material/AccessTime';
import EventBusy from '@mui/icons-material/EventBusy';
import InsertDriveFile from '@mui/icons-material/InsertDriveFile';
import MenuBook from '@mui/icons-material/MenuBook';
import Inventory2 from '@mui/icons-material/Inventory2';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import { Section } from '../../../components/ui/Section';
import {
  client, MarketSupplier, Instance, Market, MarketCatalogue, MarketItem,
} from '../../../app/services/api/orderManagementClient';
import { applyRoundAndFormat } from '../../../utils';
import useLogError from '../../../hooks/useLogError';
import { useInstanceMarket } from '../../../contexts/InstanceMarketContext';
import { DetailPageTemplate } from '../../../components/page-templates/detail-page/DetailPageTemplate';
import type { TabDefinition } from '../../../components/shared/tabs/TabbedSection';
import { InsightTile } from '../../../components/shared/tiles/InsightTile';
import { TableSection, TableColumn } from '../../../components/shared/tables/TableSection';

const SupplierForm = () => {
  const { t } = useTranslation('suppliers');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const { logError, extractErrorMessage } = useLogError();
  const isEdit = Boolean(id);
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { instances, getMarkets, ensureMarketsLoaded } = useInstanceMarket();
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(true);


  const companyId = searchParams.get('companyId');
  const customerTypeId = searchParams.get('customerTypeId');
  const searchTerm = searchParams.get('searchTerm');
  
  const [formData, setFormData] = useState<MarketSupplier>({
    supplierId: 0,
    instanceName: '',
    companyId: 0,
    marketName: '',
    customerTypeId: 0,
    supplierNumber: '',
    supplierName: '',
    address1: '',
    address2: '',
    town: '',
    county: '',
    postcode: '',
    country: '',
    emailAddress: '',
    emailAddress2: '',
    fileFormat: '',
    publishMethod: '',
    supplierNumber2: '',
    cutOffTime: '',
    fileContents: 0,
    creditRequestCutOffTime: '',
    minimumOrderValue: 0,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Headline tiles/tabs are read-only summary data layered onto the existing form - they never affect save/update behaviour.
  const canViewSupplierCatalogues = hasPermissionTo(['CatalogueView', 'ConfigurationAdmin']);
  const canEditCatalogues = hasPermissionTo(['CatalogueUpdate', 'ConfigurationAdmin']);
  const canViewSupplierProducts = hasPermissionTo(['ProductView', 'ConfigurationAdmin']);

  const [marketCatalogues, setMarketCatalogues] = useState<MarketCatalogue[]>([]);
  const [isCataloguesLoading, setIsCataloguesLoading] = useState<boolean>(false);
  const [marketItems, setMarketItems] = useState<MarketItem[]>([]);
  const [isItemsLoading, setIsItemsLoading] = useState<boolean>(false);
  const [cataloguesPage, setCataloguesPage] = useState<number>(0);
  const [cataloguesSearchInput, setCataloguesSearchInput] = useState<string>('');

  const markets = getMarkets(formData.companyId ? formData.companyId.toString() : '');

  const supplierCatalogues = useMemo(() => (
    canViewSupplierCatalogues
      ? marketCatalogues.filter((catalogue) => (catalogue.supplierIds ?? []).includes(formData.supplierId))
      : []
  ), [canViewSupplierCatalogues, marketCatalogues, formData.supplierId]);

  const supplierProductOverrides = useMemo(() => (
    canViewSupplierProducts
      ? marketItems.filter((item) => (item.contentSuppliers ?? []).some((cs) => cs.supplierId === formData.supplierId))
      : []
  ), [canViewSupplierProducts, marketItems, formData.supplierId]);

  const filteredSupplierCatalogues = useMemo(() => {
    const search = cataloguesSearchInput.trim().toLowerCase();
    if (!search) return supplierCatalogues;
    return supplierCatalogues.filter((catalogue) => catalogue.name?.toLowerCase().includes(search));
  }, [supplierCatalogues, cataloguesSearchInput]);

  const cataloguesPageSize = 10;
  const cataloguesTotalPages = Math.max(1, Math.ceil(filteredSupplierCatalogues.length / cataloguesPageSize));
  const pagedSupplierCatalogues = filteredSupplierCatalogues.slice(
    cataloguesPage * cataloguesPageSize,
    (cataloguesPage + 1) * cataloguesPageSize,
  );

  const selectedMarket = markets.find((market) => market.customerTypeId === formData.customerTypeId);

  useEffect(() => {
    if (!isEdit || !canViewSupplierCatalogues || !formData.companyId || !formData.customerTypeId) return;
    setIsCataloguesLoading(true);
    client.getMarketCatalogues({
      companyId: formData.companyId.toString(),
      customerTypeId: formData.customerTypeId.toString(),
      searchTerm: '',
      sort: '',
      direction: '',
      pageNumber: 0,
      pageSize: 2147483647,
    })
      .then((result) => {
        if (result.success && result.data) {
          setMarketCatalogues(result.data.items ?? []);
        }
      })
      .catch((error) => console.error('Error fetching market catalogues:', error))
      .finally(() => setIsCataloguesLoading(false));
  }, [isEdit, canViewSupplierCatalogues, formData.companyId, formData.customerTypeId]);

  useEffect(() => {
    if (!isEdit || !canViewSupplierProducts || !formData.companyId || !formData.customerTypeId) return;
    setIsItemsLoading(true);
    client.getMarketItems({
      companyId: formData.companyId.toString(),
      customerTypeId: formData.customerTypeId.toString(),
      searchTerm: '',
      sort: '',
      direction: '',
      pageNumber: 0,
      pageSize: 2147483647,
    })
      .then((result) => {
        if (result.success && result.data) {
          setMarketItems(result.data.items ?? []);
        }
      })
      .catch((error) => console.error('Error fetching market items:', error))
      .finally(() => setIsItemsLoading(false));
  }, [isEdit, canViewSupplierProducts, formData.companyId, formData.customerTypeId]);

  useEffect(() => {
    setCataloguesPage(0);
  }, [cataloguesSearchInput]);

  const fileFormatOptions = [
    { value: 'CSV', label: 'CSV' },
    { value: 'XLS', label: 'XLS' },
    { value: 'XLSX', label: 'XLSX' },
    { value: 'PDF', label: 'PDF' },
  ];

  const publishMethodOptions = [
    { value: 'EMAIL', label: 'EMAIL' },
    { value: 'FTP', label: 'FTP' },
    { value: 'FTP & EMAIL', label: 'FTP & EMAIL' },
  ];

  const fileContentsOptions = [
    { value: 0, label: t('consolidatedFile') },
    { value: 1, label: t('singleFile') },
  ];

  const fetchSupplier = async () => {
    if (!id) return;
    
    setIsLoading(true);
    try {
      const result = await client.getMarketSupplier(id);
      if (result.success && result.data) {
        setFormData(result.data);
        if (result.data.companyId) {
          ensureMarketsLoaded(result.data.companyId.toString());
        }
      }
    } catch (error) {
      console.error('Error fetching supplier:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchSupplier();
    }
  }, [id, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.supplierName.trim()) {
      newErrors.supplierName = t('supplierNameRequired');
    }

    if (!formData.supplierNumber.trim()) {
      newErrors.supplierNumber = t('supplierNumberRequired');
    }

    if (!formData.companyId) {
      newErrors.companyId = t('instanceRequired');
    }

    if (!formData.customerTypeId) {
      newErrors.customerTypeId = t('marketRequired');
    }

    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = t('emailAddressRequired');
    }

    if (!formData.fileFormat) {
      newErrors.fileFormat = t('fileFormatRequired');
    }

    if (!formData.publishMethod) {
      newErrors.publishMethod = t('publishMethodRequired');
    }

    if (formData.minimumOrderValue !== undefined && formData.minimumOrderValue < 0) {
      newErrors.minimumOrderValue = t('minimumOrderValueMustBePositive');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof MarketSupplier) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let value: string | number = event.target.value;
    
    if (field === 'supplierId' || field === 'companyId' || field === 'customerTypeId' || field === 'fileContents' || field === 'minimumOrderValue') {
      value = value === '' ? 0 : parseInt(value) || 0;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleInstanceChange = (event: React.SyntheticEvent, value: Instance | null) => {
    setFormData(prev => ({
      ...prev,
      companyId: value?.id || 0,
      instanceName: value?.name || '',
      customerTypeId: 0, // Reset market when instance changes
      marketName: '',
    }));
    
    if (value?.id) {
      ensureMarketsLoaded(value.id.toString());
    }
    
    if (errors.companyId) {
      setErrors(prev => ({
        ...prev,
        companyId: '',
      }));
    }
  };

  const handleMarketChange = (event: React.SyntheticEvent, value: Market | null) => {
    setFormData(prev => ({
      ...prev,
      customerTypeId: value?.customerTypeId || 0,
      marketName: value?.name || '',
    }));
    
    if (errors.customerTypeId) {
      setErrors(prev => ({
        ...prev,
        customerTypeId: '',
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (isEdit) {
        const result = await client.updateMarketSupplier(id!, formData);
        if (result.success) {
          setMessage(t('updateSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/procurement/suppliers${params.toString() ? `?${params.toString()}` : ''}`);
          }, 500);
        } else if (result.message) {
          setMessage(result.message);
          setIsSuccess(false);
          setShowMessage(true);
        }
      } else {
        const result = await client.createMarketSupplier(formData);
        if (result.success) {
          setMessage(t('createSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/procurement/suppliers${params.toString() ? `?${params.toString()}` : ''}`);
          }, 500);
        } else if (result.message) {
          setMessage(result.message);
          setIsSuccess(false);
          setShowMessage(true);
        }
      }
    } catch (error) {
      logError(error);
      const errorMessage = extractErrorMessage(error);
      setMessage(errorMessage || (isEdit ? t('updateErrorMessage') : t('createErrorMessage')));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const getUrlParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    if (customerTypeId) params.set('customerTypeId', customerTypeId);
    if (searchTerm) params.set('searchTerm', searchTerm);

    return params;
  };

  const handleCancel = () => {
    const params = getUrlParams();
    navigate(`/procurement/suppliers${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const userCanSave = hasPermissionTo([isEdit ? 'SupplierUpdate' : 'SupplierCreate', 'ConfigurationAdmin']);

  const truncateText = (text: string, maxLength: number = 40) => {
    if (!text) return '';
    return text.length <= maxLength ? text : `${text.substring(0, maxLength)}...`;
  };

  // Cut off time is stored as a plain HH:mm plus a separate timezone offset - adjust and label it for display.
  const formatSupplierCutOffTime = (time?: string) => {
    if (!time) return t('procurementTileNotSet', 'Not set');

    if (formData.timezoneOffset !== undefined && formData.timezoneOffset !== null) {
      const [hours, minutes] = time.split(':').map(Number);
      const totalMinutes = hours * 60 + minutes + Number(formData.timezoneOffset) * 60;
      const adjustedTotalMinutes = ((totalMinutes % 1440) + 1440) % 1440;
      const adjustedHours = Math.floor(adjustedTotalMinutes / 60);
      const adjustedMinutes = adjustedTotalMinutes % 60;
      const formatted = `${String(adjustedHours).padStart(2, '0')}:${String(adjustedMinutes).padStart(2, '0')}`;
      return formData.timezone ? `${formatted} (${formData.timezone})` : formatted;
    }

    return formData.timezone ? `${time} (${formData.timezone})` : time;
  };

  const handleViewCatalogue = (catalogue: MarketCatalogue) => {
    if (!canEditCatalogues) return;
    const params = new URLSearchParams();
    params.set('companyId', formData.companyId.toString());
    if (formData.customerTypeId) params.set('customerTypeId', formData.customerTypeId.toString());
    navigate(`/procurement/catalogues/edit/${catalogue.customerGroupId}?${params.toString()}`);
  };

  const catalogueColumns: TableColumn<MarketCatalogue>[] = [
    {
      id: 'name',
      label: t('procurementCatalogueName', 'Catalogue'),
      render: (row) => (
        <Tooltip title={row.name}
          placement="top">
          <span>{truncateText(row.name)}</span>
        </Tooltip>
      ),
    },
  ];

  const overviewTabContent = (
    <Grid container
      spacing={4}>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('supplierName')}
          value={formData.supplierName}
          onChange={handleInputChange('supplierName')}
          error={!!errors.supplierName}
          helperText={errors.supplierName}
          fullWidth
          required
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('supplierNumber')}
          value={formData.supplierNumber}
          onChange={handleInputChange('supplierNumber')}
          error={!!errors.supplierNumber}
          helperText={errors.supplierNumber}
          fullWidth
          required
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('supplierNumber2')}
          value={formData.supplierNumber2}
          onChange={handleInputChange('supplierNumber2')}
          fullWidth
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <Autocomplete
          options={instances}
          getOptionLabel={(option) => option.name}
          value={instances.find(instance => instance.id === formData.companyId) || null}
          onChange={handleInstanceChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('instance')}
              error={!!errors.companyId}
              helperText={errors.companyId}
              required
            />
          )}
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <Autocomplete
          options={markets}
          getOptionLabel={(option) => option.name}
          value={markets.find(market => market.customerTypeId === formData.customerTypeId) || null}
          onChange={handleMarketChange}
          disabled={!formData.companyId}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('market')}
              error={!!errors.customerTypeId}
              helperText={errors.customerTypeId}
              required
            />
          )}
        />
      </Grid>
    </Grid>
  );

  const orderingTabContent = (
    <Grid container
      spacing={4}>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('fileFormat')}
          select
          value={formData.fileFormat}
          onChange={handleInputChange('fileFormat')}
          error={!!errors.fileFormat}
          helperText={errors.fileFormat}
          fullWidth
          required
        >
          {fileFormatOptions.map((option) => (
            <MenuItem key={option.value}
              value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('fileContents')}
          select
          value={formData.fileContents !== undefined ? formData.fileContents : 0}
          onChange={handleInputChange('fileContents')}
          fullWidth
        >
          {fileContentsOptions.map((option) => (
            <MenuItem key={option.value}
              value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('publishMethod')}
          select
          value={formData.publishMethod}
          onChange={handleInputChange('publishMethod')}
          error={!!errors.publishMethod}
          helperText={errors.publishMethod}
          fullWidth
          required
        >
          {publishMethodOptions.map((option) => (
            <MenuItem key={option.value}
              value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('cutOffTime')}
          type="time"
          value={formData.cutOffTime || ''}
          onChange={handleInputChange('cutOffTime')}
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('creditRequestCutOffTime')}
          type="time"
          value={formData.creditRequestCutOffTime || ''}
          onChange={handleInputChange('creditRequestCutOffTime')}
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('minimumOrderValue')}
          type="number"
          value={formData.minimumOrderValue || ''}
          onChange={handleInputChange('minimumOrderValue')}
          error={!!errors.minimumOrderValue}
          helperText={errors.minimumOrderValue}
          fullWidth
          inputProps={{ min: 0, step: 1 }}
        />
      </Grid>
    </Grid>
  );

  const contactsTabContent = (
    <Grid container
      spacing={4}>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('emailAddress')}
          type="email"
          value={formData.emailAddress}
          onChange={handleInputChange('emailAddress')}
          error={!!errors.emailAddress}
          helperText={errors.emailAddress}
          fullWidth
          required
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('emailAddress2')}
          type="email"
          value={formData.emailAddress2}
          onChange={handleInputChange('emailAddress2')}
          fullWidth
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('address1')}
          value={formData.address1}
          onChange={handleInputChange('address1')}
          fullWidth
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('address2')}
          value={formData.address2}
          onChange={handleInputChange('address2')}
          fullWidth
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('town')}
          value={formData.town}
          onChange={handleInputChange('town')}
          fullWidth
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('county')}
          value={formData.county}
          onChange={handleInputChange('county')}
          fullWidth
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('postcode')}
          value={formData.postcode}
          onChange={handleInputChange('postcode')}
          fullWidth
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('country')}
          value={formData.country}
          onChange={handleInputChange('country')}
          fullWidth
        />
      </Grid>
    </Grid>
  );

  const cataloguesTabContent = !isEdit ? (
    <Section style={{ mt: 0, p: 5 }}>
      <Box sx={{ p: { xs: 4, md: 5 } }}>
        <Typography variant="textSM"
          color="text.secondary">
          {t('procurementCataloguesAvailableAfterCreate', 'Catalogues using this supplier will show here once it has been created.')}
        </Typography>
      </Box>
    </Section>
  ) : !canViewSupplierCatalogues ? (
    <Section style={{ mt: 0, p: 5 }}>
      <Box sx={{ p: { xs: 4, md: 5 } }}>
        <Typography variant="textSM"
          color="text.secondary">
          {t('procurementCataloguesPermissionRequired', 'You do not have permission to view catalogues.')}
        </Typography>
      </Box>
    </Section>
  ) : (
    <TableSection<MarketCatalogue>
      title={t('procurementCatalogues', 'Catalogues')}
      columns={catalogueColumns}
      rows={pagedSupplierCatalogues}
      getRowId={(row) => row.customerGroupId}
      onRowClick={canEditCatalogues ? handleViewCatalogue : undefined}
      recordCount={filteredSupplierCatalogues.length}
      recordCountLabel={t('procurementCatalogues', 'Catalogues')}
      loading={isCataloguesLoading}
      searchValue={cataloguesSearchInput}
      onSearchChange={setCataloguesSearchInput}
      searchPlaceholder={t('procurementSearchCataloguesPlaceholder', 'Search catalogues...')}
      page={cataloguesPage}
      maxPages={cataloguesTotalPages}
      onPageChange={setCataloguesPage}
    />
  );

  const tabs: TabDefinition[] = [
    {
      id: 'overview',
      label: t('overview', 'Overview'),
      content: overviewTabContent,
    },
    {
      id: 'ordering',
      label: t('ordering', 'Ordering'),
      content: orderingTabContent,
    },
    {
      id: 'contacts',
      label: t('contacts', 'Contacts'),
      content: contactsTabContent,
    },
    {
      id: 'catalogues',
      label: t('procurementCatalogues', 'Catalogues'),
      content: cataloguesTabContent,
    },
  ];

  const fileDeliveryValue = formData.fileFormat && formData.publishMethod
    ? `${formData.fileFormat} ${t('procurementTileFileDeliveryVia', 'via')} ${formData.publishMethod}`
    : (formData.fileFormat || formData.publishMethod || t('procurementTileNotSet', 'Not set'));

  return (
    <DetailPageTemplate
      title={t(isEdit ? 'editSupplier' : 'createSupplier')}
      subtitle={t('supplierFormSubtitle', 'Manage supplier setup, delivery rules, ordering preferences, and contact details.')}
      loading={isLoading}
      loadingLabel={t('loading')}
      actions={(
        <>
          <Button
            variant="secondary"
            size="lg"
            onClick={handleCancel}
          >
            {t('cancel')}
          </Button>
          {userCanSave && (
            <Button
              variant="primary"
              size="lg"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? t('saving') : (isEdit ? t('update') : t('create'))}
            </Button>
          )}
        </>
      )}
      headlineContent={{
        layout: 'six',
        insights: [
          <InsightTile
            key="minimumOrderValue"
            label={t('minimumOrderValue', 'Minimum Order Value')}
            value={formData.minimumOrderValue
              ? applyRoundAndFormat(formData.minimumOrderValue, selectedMarket?.currencyCode)
              : t('procurementTileNotSet', 'Not set')}
            description={t('procurementTileMinimumOrderValueDescription', 'Minimum value required to place an order')}
            icon={<AttachMoney />}
            variant="success"
          />,
          <InsightTile
            key="cutoffTime"
            label={t('cutOffTime', 'Cut Off Time')}
            value={formatSupplierCutOffTime(formData.cutOffTime)}
            description={t('procurementTileCutoffDescription', 'Orders must be placed before this time')}
            icon={<AccessTime />}
            variant="primary"
          />,
          <InsightTile
            key="creditRequestCutoffTime"
            label={t('creditRequestCutOffTime', 'Credit Request Cut Off Time')}
            value={formatSupplierCutOffTime(formData.creditRequestCutOffTime)}
            description={t('procurementTileCreditCutoffDescription', 'Credit requests must be submitted before this time')}
            icon={<EventBusy />}
            variant="secondary"
          />,
          <InsightTile
            key="fileDelivery"
            label={t('procurementTileFileDeliveryLabel', 'File Delivery')}
            value={fileDeliveryValue}
            description={t('procurementTileFileDeliveryDescription', 'How order files are delivered to this supplier')}
            icon={<InsertDriveFile />}
            variant="primary"
          />,
          <InsightTile
            key="catalogues"
            label={t('procurementTileCataloguesLabel', 'Catalogues')}
            value={canViewSupplierCatalogues ? supplierCatalogues.length : '—'}
            description={t('procurementTileCataloguesDescription', 'Catalogues that use this supplier for DSD ordering')}
            icon={<MenuBook />}
            variant="primary"
            loading={isEdit && canViewSupplierCatalogues && isCataloguesLoading}
          />,
          <InsightTile
            key="products"
            label={t('procurementTileProductsLabel', 'Products')}
            value={canViewSupplierProducts ? supplierProductOverrides.length : '—'}
            description={t('procurementTileProductsDescription', 'Products with a price or item number override for this supplier')}
            icon={<Inventory2 />}
            variant="secondary"
            loading={isEdit && canViewSupplierProducts && isItemsLoading}
          />,
        ],
      }}
      tabs={tabs}
      tabsAriaLabel={t('supplierName')}
    >
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSuccess ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </DetailPageTemplate>
  );
};

export default SupplierForm;
